import _ from 'lodash';

import {
  AudienceAgeWithinTargetingAttributeFragment,
  AudienceAggregatedAudienceTargetingAttributeFragment,
  AudienceCommunicationPreferenceTargetingAttributeFragment,
  AudienceFragment,
  AudienceFuelAndConvenienceProfileTargetingAttributeFragment,
  AudienceInAllEventTargetingAttributeFragment,
  AudienceInAllProductInterestsTargetingAttributeFragment,
  AudienceInAllSegmentsTargetingAttributeFragment,
  AudienceInAnyEventTargetingAttributeFragment,
  AudienceInAnyProductInterestsTargetingAttributeFragment,
  AudienceInAnySegmentsTargetingAttributeFragment,
  AudienceNotInAllEventTargetingAttributeFragment,
  AudienceNotInAllProductInterestsTargetingAttributeFragment,
  AudienceNotInAnyEventTargetingAttributeFragment,
  AudienceNotInAnyProductInterestsTargetingAttributeFragment,
  AudienceNotInAnySegmentsTargetingAttributeFragment,
  AudienceOfferFulfillmentTargetingAttributeFragment,
  AudiencePointBalanceTargetingAttributeFragment,
  AudiencePurchasedAtLeastTargetingAttributeFragment,
  AudiencePurchasedAtMostTargetingAttributeFragment,
  AudienceRetailerNetworkSiteUsersTargetingAttributeFragment,
  AudiencesQuery,
  AudienceStoreLocationTargetingAttributeFragment,
  AudienceSurveyResponseTargetingAttributeFragment,
  AudienceTimeSinceLastVisitTargetingAttributeFragment,
  AudienceTimeSinceMembershipAcquisitionTargetingAttributeFragment,
  AudienceWalletSteeringTargetingAttributeFragment,
  AudienceExternalIdentityVerificationTargetingAttributeFragment,
  PartialAudienceFragment,
} from '@occc/graphql';

export const isNotUndefined = <T>(item: T | undefined): item is T => !!item;

export type GroupAudienceTargetingAttributesByType = ReturnType<
  typeof groupAudienceTargetingAttributesByType
>;

export const groupAudienceTargetingAttributesByType = (audience?: AudienceFragment) => {
  const {
    AudienceAgeWithinTargetingAttribute,
    AudiencePurchasedAtMostTargetingAttribute,
    AudiencePurchasedAtLeastTargetingAttribute,
    AudienceInAllSegmentsTargetingAttribute,
    AudienceInAnySegmentsTargetingAttribute,
    AudienceFuelAndConvenienceProfileTargetingAttribute,
    AudienceOfferFulfillmentTargetingAttribute,
    AudienceNotInAnySegmentsTargetingAttribute,
    AudienceStoreLocationTargetingAttribute,
    AudienceWalletSteeringTargetingAttribute,
    AudienceInAllProductInterestsTargetingAttribute,
    AudienceInAnyProductInterestsTargetingAttribute,
    AudienceNotInAllProductInterestsTargetingAttribute,
    AudienceNotInAnyProductInterestsTargetingAttribute,
    AudienceInAllEventTargetingAttribute,
    AudienceInAnyEventTargetingAttribute,
    AudienceNotInAllEventTargetingAttribute,
    AudienceNotInAnyEventTargetingAttribute,
    AudienceTimeSinceLastVisitTargetingAttribute,
    AudiencePointBalanceTargetingAttribute,
    AudienceTimeSinceMembershipAcquisitionTargetingAttribute,
    AudienceRetailerNetworkSiteUsersTargetingAttribute,
    AudienceSurveyResponseTargetingAttribute,
    AudienceAggregatedAudiencesTargetingAttribute,
    AudienceExternalIdentityVerificationTargetingAttribute,
    AudienceCommunicationPreferenceTargetingAttribute,
  } = _.groupBy(audience?.targetingAttributes, '__typename');

  const lastVisit =
    AudienceTimeSinceLastVisitTargetingAttribute &&
    (AudienceTimeSinceLastVisitTargetingAttribute[0] as AudienceTimeSinceLastVisitTargetingAttributeFragment);

  const ageWithin = (AudienceAgeWithinTargetingAttribute ||
    []) as AudienceAgeWithinTargetingAttributeFragment[];

  const purchases = _.concat(
    AudiencePurchasedAtMostTargetingAttribute,
    AudiencePurchasedAtLeastTargetingAttribute,
  ).filter(Boolean) as (
    | AudiencePurchasedAtLeastTargetingAttributeFragment
    | AudiencePurchasedAtMostTargetingAttributeFragment
  )[];

  const segments = _.concat(
    AudienceInAllSegmentsTargetingAttribute,
    AudienceInAnySegmentsTargetingAttribute,
    AudienceNotInAnySegmentsTargetingAttribute,
  ).filter(Boolean) as (
    | AudienceInAllSegmentsTargetingAttributeFragment
    | AudienceInAnySegmentsTargetingAttributeFragment
    | AudienceNotInAnySegmentsTargetingAttributeFragment
  )[];

  const audienceSiteUsersTargetingAttribute =
    AudienceRetailerNetworkSiteUsersTargetingAttribute &&
    (AudienceRetailerNetworkSiteUsersTargetingAttribute[0] as AudienceRetailerNetworkSiteUsersTargetingAttributeFragment);

  const employeeTargetingAttributes = audienceSiteUsersTargetingAttribute
    ? {
        retailerNetworkUserType: audienceSiteUsersTargetingAttribute.retailerNetworkUserTypes[0],
        operator: audienceSiteUsersTargetingAttribute.retailerNetworkOperator,
        retailerNetworks: audienceSiteUsersTargetingAttribute.retailerNetworkUuids,
      }
    : undefined;

  const fuelAndConvenienceProfile = (AudienceFuelAndConvenienceProfileTargetingAttribute ||
    []) as AudienceFuelAndConvenienceProfileTargetingAttributeFragment[];

  const fulfillments = (AudienceOfferFulfillmentTargetingAttribute ||
    []) as AudienceOfferFulfillmentTargetingAttributeFragment[];

  const homeStore = (AudienceStoreLocationTargetingAttribute ||
    []) as AudienceStoreLocationTargetingAttributeFragment[];

  const walletSteeringItems = (AudienceWalletSteeringTargetingAttribute ||
    []) as AudienceWalletSteeringTargetingAttributeFragment[];

  const productInterests = _.concat(
    AudienceInAllProductInterestsTargetingAttribute,
    AudienceInAnyProductInterestsTargetingAttribute,
    AudienceNotInAllProductInterestsTargetingAttribute,
    AudienceNotInAnyProductInterestsTargetingAttribute,
  ).filter(Boolean) as (
    | AudienceInAllProductInterestsTargetingAttributeFragment
    | AudienceInAnyProductInterestsTargetingAttributeFragment
    | AudienceNotInAllProductInterestsTargetingAttributeFragment
    | AudienceNotInAnyProductInterestsTargetingAttributeFragment
  )[];

  const qualifiedEvents = _.concat(
    AudienceInAllEventTargetingAttribute,
    AudienceInAnyEventTargetingAttribute,
    AudienceNotInAllEventTargetingAttribute,
    AudienceNotInAnyEventTargetingAttribute,
  ).filter(Boolean) as (
    | AudienceInAllEventTargetingAttributeFragment
    | AudienceInAnyEventTargetingAttributeFragment
    | AudienceNotInAllEventTargetingAttributeFragment
    | AudienceNotInAnyEventTargetingAttributeFragment
  )[];

  const pointBalanceItems = (AudiencePointBalanceTargetingAttribute ||
    []) as AudiencePointBalanceTargetingAttributeFragment[];
  const pointBalance = pointBalanceItems.length > 0 ? pointBalanceItems[0] : undefined;

  const membershipCreatedItems = (AudienceTimeSinceMembershipAcquisitionTargetingAttribute ||
    []) as AudienceTimeSinceMembershipAcquisitionTargetingAttributeFragment[];
  const membershipCreated =
    membershipCreatedItems.length > 0 ? membershipCreatedItems[0] : undefined;

  const surveyItems = (AudienceSurveyResponseTargetingAttribute ||
    []) as AudienceSurveyResponseTargetingAttributeFragment[];

  const combinedAudiencesItems = (AudienceAggregatedAudiencesTargetingAttribute ||
    []) as AudienceAggregatedAudienceTargetingAttributeFragment[];
  const combinedAudiences =
    combinedAudiencesItems.length > 0 ? combinedAudiencesItems[0] : undefined;

  const externalIdentityVerification = (AudienceExternalIdentityVerificationTargetingAttribute ||
    []) as AudienceExternalIdentityVerificationTargetingAttributeFragment[];

  const communicationPreference = (AudienceCommunicationPreferenceTargetingAttribute ||
    []) as AudienceCommunicationPreferenceTargetingAttributeFragment[];

  return {
    ageWithin,
    purchases,
    segments,
    fuelAndConvenienceProfile,
    fulfillments,
    homeStore,
    walletSteeringItems,
    productInterests,
    qualifiedEvents,
    lastVisit,
    pointBalance,
    membershipCreated,
    employeeTargetingAttributes,
    surveyItems,
    combinedAudiences,
    externalIdentityVerification,
    communicationPreference,
  };
};

export const transformDataToAudiencesArray = (data: AudiencesQuery): PartialAudienceFragment[] => {
  const { edges } = data.audiences;
  return edges.map((item) => item.node);
};
